// Lote V-11.A.2 — Web client para os endpoints admin de facturación.

import { apiClient } from './client';
import type {
  IssuerActivationCheckResponseSchema,
  IssuerSetting,
  IssuerSettingUpdate,
  InvoiceListItem,
  InvoiceListResponseSchema,
  InvoiceStatsResponseSchema,
} from '@leyrochain/shared';
import type { z } from 'zod';

type ActivationCheck = z.infer<typeof IssuerActivationCheckResponseSchema>;
type InvoiceList = z.infer<typeof InvoiceListResponseSchema>;
type InvoiceStats = z.infer<typeof InvoiceStatsResponseSchema>;

export type { IssuerSetting, IssuerSettingUpdate, InvoiceListItem };

// ─── IssuerSetting ────────────────────────────────────────────────────────

export async function getIssuerSettings(): Promise<IssuerSetting> {
  const { data } = await apiClient.get<IssuerSetting>('/api/v1/admin/issuer-settings');
  return data;
}

export async function updateIssuerSettings(patch: IssuerSettingUpdate): Promise<IssuerSetting> {
  const { data } = await apiClient.put<IssuerSetting>('/api/v1/admin/issuer-settings', patch);
  return data;
}

export async function getActivationCheck(): Promise<ActivationCheck> {
  const { data } = await apiClient.get<ActivationCheck>(
    '/api/v1/admin/issuer-settings/activation-check',
  );
  return data;
}

export async function activateIssuer(): Promise<IssuerSetting> {
  const { data } = await apiClient.post<IssuerSetting>('/api/v1/admin/issuer-settings/activate', {
    confirm: true,
  });
  return data;
}

export async function deactivateIssuer(): Promise<IssuerSetting> {
  const { data } = await apiClient.post<IssuerSetting>('/api/v1/admin/issuer-settings/deactivate');
  return data;
}

// ─── Invoices ─────────────────────────────────────────────────────────────

export interface ListInvoicesParams {
  from?: string;
  to?: string;
  type?: 'STANDARD' | 'RECTIFICATIVE';
  recipient_role?: 'SELLER' | 'BUYER';
  email_status?: 'PENDING' | 'SENT' | 'BOUNCED' | 'FAILED' | 'SKIPPED';
  search?: string;
  page?: number;
  page_size?: number;
}

export async function listInvoices(params: ListInvoicesParams = {}): Promise<InvoiceList> {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') search.set(k, String(v));
  }
  const qs = search.toString();
  const { data } = await apiClient.get<InvoiceList>(`/api/v1/admin/invoices${qs ? `?${qs}` : ''}`);
  return data;
}

export async function getInvoiceStats(): Promise<InvoiceStats> {
  const { data } = await apiClient.get<InvoiceStats>('/api/v1/admin/invoices/stats');
  return data;
}

/**
 * Trigger download do CSV no navegador. Constrúe URL coa pasada actual de
 * filtros e dispara unha navegación cun blob.
 */
export async function downloadInvoicesCsv(params: ListInvoicesParams = {}): Promise<void> {
  const search = new URLSearchParams();
  if (params.from) search.set('from', params.from);
  if (params.to) search.set('to', params.to);
  if (params.type) search.set('type', params.type);
  const qs = search.toString();
  const res = await apiClient.get<string>(
    `/api/v1/admin/invoices/export.csv${qs ? `?${qs}` : ''}`,
    { responseType: 'text' },
  );
  // Blob + download programático.
  const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `leyrochain-invoices-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export async function voidInvoice(
  id: string,
  reason: string,
): Promise<{ rectificative_id: string; rectificative_number: string }> {
  const { data } = await apiClient.post<{
    rectificative_id: string;
    rectificative_number: string;
  }>(`/api/v1/admin/invoices/${encodeURIComponent(id)}/void`, { reason });
  return data;
}

export async function resendInvoiceEmail(id: string): Promise<void> {
  await apiClient.post(`/api/v1/admin/invoices/${encodeURIComponent(id)}/resend-email`);
}

/** Triggers XLSX download. */
export async function downloadInvoicesXlsx(params: ListInvoicesParams = {}): Promise<void> {
  const search = new URLSearchParams();
  if (params.from) search.set('from', params.from);
  if (params.to) search.set('to', params.to);
  if (params.type) search.set('type', params.type);
  const qs = search.toString();
  const res = await apiClient.get<Blob>(`/api/v1/admin/invoices/export.xlsx${qs ? `?${qs}` : ''}`, {
    responseType: 'blob',
    // Phase 27 (B-08 fix) — XLSX render can take 5-10s on large quarters
    // because exceljs walks every row. The default 15s apiClient timeout
    // is too tight; bump to 90s so the request doesn't get killed mid-build.
    timeout: 90_000,
  });
  triggerBlobDownload(res.data, 'xlsx');
}

/** Triggers ZIP download with all PDFs. */
export async function downloadInvoicesZip(params: ListInvoicesParams = {}): Promise<void> {
  const search = new URLSearchParams();
  if (params.from) search.set('from', params.from);
  if (params.to) search.set('to', params.to);
  if (params.type) search.set('type', params.type);
  const qs = search.toString();
  const res = await apiClient.get<Blob>(`/api/v1/admin/invoices/export.zip${qs ? `?${qs}` : ''}`, {
    responseType: 'blob',
    // Phase 27 (B-08 fix) — building the ZIP downloads every invoice PDF
    // from S3 sequentially before zipping. 100 invoices ≈ 30-60s. The
    // default 15s timeout killed the download silently — bump to 180s.
    timeout: 180_000,
  });
  triggerBlobDownload(res.data, 'zip');
}

/** Phase 27 (B-09) — Download a single invoice PDF.
 *  Backend resolves the canonical S3 key + streams the PDF (or 404). */
export async function downloadInvoicePdf(id: string, fullNumber: string): Promise<void> {
  const res = await apiClient.get<Blob>(`/api/v1/admin/invoices/${encodeURIComponent(id)}/pdf`, {
    responseType: 'blob',
    timeout: 60_000,
  });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${fullNumber}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

function triggerBlobDownload(blob: Blob, ext: 'xlsx' | 'zip'): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `leyrochain-invoices-${new Date().toISOString().slice(0, 10)}.${ext}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}
